import { queryClient } from 'queryClient';
import type { Currency } from '@packages/domain/models/currency';
import { hashQueryKey } from '@tanstack/react-query';
import { queryKeys } from 'hooks/queryKeys';

type Currencies = Record<string, Currency>;

const EMPTY: Currencies = {};
export const NO_CURRENCY = {} as Currency;

const listeners = new Set<() => void>();
let unsubscribeFromCache: (() => void) | null = null;

const attach = () => {
  const hash = hashQueryKey(queryKeys.currencies.all);
  unsubscribeFromCache = queryClient.getQueryCache().subscribe(event => {
    if (event.query.queryHash === hash) {
      listeners.forEach(listener => listener());
    }
  });
};

export const currenciesCache = {
  getAll: (): Currencies =>
    queryClient.getQueryData<Currencies>(queryKeys.currencies.all) ?? EMPTY,

  byCode: (code: string): Currency =>
    currenciesCache.getAll()[code] ?? NO_CURRENCY,

  merge: (currencies: Currencies): void => {
    if (Object.keys(currencies).length === 0) {
      return;
    }
    queryClient.setQueryData<Currencies>(queryKeys.currencies.all, prev => ({
      ...prev,
      ...currencies,
    }));
  },

  subscribe: (listener: () => void): (() => void) => {
    listeners.add(listener);
    if (!unsubscribeFromCache) {
      attach();
    }
    return () => {
      listeners.delete(listener);
      if (listeners.size === 0 && unsubscribeFromCache) {
        unsubscribeFromCache();
        unsubscribeFromCache = null;
      }
    };
  },
};
